import { sortProgramsByVolume } from './queensProgramVolumePriority';

/** Queen's University — grouped from queensu.ca/academics/programs (~203 entries). */
export type QueensFacultyId =
  | 'arts-science'
  | 'education'
  | 'health-sciences'
  | 'law'
  | 'smith-business'
  | 'smith-engineering'
  | 'graduate-studies';

export interface QueensFacultyOption {
  id: QueensFacultyId;
  label: string;
  programs: readonly string[];
}

export const QUEENS_FACULTY_OPTIONS: readonly QueensFacultyOption[] = [
  {
    id: 'arts-science',
    label: 'Arts and Science',
    programs: [
      'Animation Theory and Practice',
      'Art Conservation',
      'Art History',
      'Arts Leadership',
      'Astrophysics',
      'Biochemistry',
      'Biology',
      'Biology - Mathematics',
      'Biology - Psychology',
      'Biomedical Informatics',
      'Black Studies',
      'Chemistry',
      'Classical Studies',
      'Classics',
      'Cognitive Science',
      'Cultural Studies',
      'Drama',
      'Earth Systems Science',
      'Economics',
      'English',
      'Environmental Biology',
      'Environmental Chemistry',
      'Environmental Geology',
      'Environmental Life Sciences',
      'Environmental Science',
      'Environmental Studies',
      'Environmental Toxicology',
      'Film and Media',
      'Fine Art (Visual Art)',
      'French Studies',
      'Gender Studies',
      'Geographic Information Science',
      'Geography',
      'Geological Sciences',
      'Global Action and Engagement',
      'Global Development Studies',
      'History',
      'Indigenous Languages and Cultures',
      'Indigenous Studies',
      'International Studies',
      'Jewish Studies',
      'Kinesiology',
      'Languages, Literatures and Cultures',
      'Learning Language as an Additional Language',
      'Liberal Arts University Transfer (LAT)',
      'Life Sciences',
      'Linguistics',
      'Mathematics',
      'Mathematics and Statistics',
      'Media and Performance Production',
      'Mohawk Language and Culture',
      'Music',
      'Neuroscience',
      'Philosophy',
      'Physics & Astronomy',
      'Political Studies',
      'Politics, Philosophy and Economics',
      'Psychology',
      'Religious Studies',
      'Screen Cultures & Curatorial Studies',
      'Sexual and Gender Diversity',
      'Sociology',
      'Spanish and Latin American Studies',
      'Statistics',
      'Technology in Teaching',
      'Urban and Regional Planning',
      'Urban Planning Studies',
      'World Language Studies',
    ],
  },
  {
    id: 'education',
    label: 'Education',
    programs: [
      'AQ and ABQ in Education',
      'Artist in Community Education',
      'Concurrent Education',
      'Executive Education',
      'First Nations, Métis, and Inuit Studies in Education',
      'Indigenous Teacher Education',
      'Internationally Trained Teacher Education',
      'Outdoor & Experiential Education',
      'Primary-Junior French as a Second Language Education',
      'Principal\'s Qualification Program',
      'Reading and Literacy Studies in Education',
      'Special Education',
      'Teacher Leadership',
      'Teaching English as a Second Language',
      'Teaching English Language Learners',
      'Teaching Mathematics',
      'Technological Education',
      'Workplace Training and Development',
      'World Indigenous Studies in Education',
    ],
  },
  {
    id: 'health-sciences',
    label: 'Health Sciences',
    programs: [
      'Anatomical Sciences',
      'Anesthesiology',
      'Bachelor of Health Sciences',
      'Cardiology',
      'Critical Care Medicine',
      'Diagnostic and Molecular Pathology',
      'Diagnostic Radiology',
      'Disability and Physical Activity',
      'Emergency Medicine',
      'Endocrinology & Metabolism',
      'Family Medicine',
      'Gastroenterology',
      'General Internal Medicine',
      'Hematology',
      'Infectious Diseases',
      'Internal Medicine',
      'MBA + Healthcare Quality',
      'Medicine',
      'Nephrology',
      'Neurology',
      'Nursing',
      'Obstetrics and Gynaecology',
      'Occupational Therapy',
      'Oncology',
      'Ophthalmology',
      'Palliative Medicine',
      'Pathology and Molecular Medicine',
      'Pediatrics',
      'Physical Medicine and Rehabilitation',
      'Physical Therapy',
      'Psychiatry',
      'Public Health and Preventive Medicine',
      'Public Health Sciences',
      'Respirology and Sleep Medicine',
      'Rheumatology',
      'Surgery',
      'Urology',
      'Water and Human Health',
    ],
  },
  {
    id: 'law',
    label: 'Law',
    programs: [
      'Business / Commerce + Law',
      'Certificate in Law',
      'Civil Law / Common Law',
      'Commerce + Law (BCom/JD)',
      'Economics + Law (MA(Econ)/JD)',
      'Immigration and Citizenship Law',
      'International Law Programs at Bader College',
      'Juris Doctor',
      'Law + Business MBA and GBD (JD/MBA & GDB)',
      'Law + Industrial Relations (JD/MIR)',
      'Law + Public Administration (JD/MPA)',
      'LLM Program',
      'LLM with a Specialization in Political and Legal Thought',
      'MBA + Law',
    ],
  },
  {
    id: 'smith-business',
    label: 'Smith Business',
    programs: [
      'Accounting',
      'Advanced Executive',
      'Advanced Human Resources Certificate',
      'Advanced Labour Relations Certificate',
      'Analytics',
      'Artificial Intelligence (Management)',
      'Arts Management',
      'Business / Commerce',
      'Data Analytics',
      'Digital Product Management',
      'Employment Relations',
      'Enterprise Sales',
      'Entrepreneurship, Innovation & Creativity',
      'Finance',
      'Financial Innovation & Technology',
      'Industrial Relations',
      'International Business',
      'Labour Relations Certificate',
      'Management',
      'Management Innovation & Entrepreneurship',
      'MBA + Analytics',
      'MBA + Artificial Intelligence',
      'MBA + Digital Product Management',
      'MBA + Finance',
      'MBA / Business Administration',
      'Organization Development Fundamentals Certificate',
      'Pharmaceutical and Healthcare Management and Innovation',
      'Professional Editing Standards',
      'Professional Impact Analysis',
      'Public Administration',
      'Public Executive',
      'Social Performance Management in the Extractive Industries',
    ],
  },
  {
    id: 'smith-engineering',
    label: 'Smith Engineering',
    programs: [
      'Advanced Design & Manufacturing Institute',
      'Artificial Intelligence (Engineering)',
      'Biomedical Computing',
      'Biomedical Engineering',
      'Biotechnology',
      'Chemical Engineering',
      'Civil Engineering',
      'Computer Engineering',
      'Computing',
      'Computing and the Creative Arts',
      'Computing, Mathematics, and Analytics',
      'Electrical Engineering',
      'Engineering and Applied Physics',
      'Engineering Chemistry',
      'Engineering Physics',
      'GeoEngineering',
      'Geological Engineering',
      'Mathematical Physics',
      'Mathematics & Engineering',
      'Mechanical and Materials Engineering',
      'Mechatronics and Robotics Engineering',
      'Mining Engineering',
      'Mining Technologies',
      'Nuclear Engineering (UNENE)',
      'Physics, Engineering Physics & Astronomy',
      'Software Design',
    ],
  },
  {
    id: 'graduate-studies',
    label: 'Graduate Studies',
    programs: [
      'Advanced Leadership for Social Impact',
      'Aging and Health',
      'Biomedical and Molecular Sciences',
      'Cancer Research',
      'Earth and Energy Resources Leadership',
      'Master of Health Professions Education',
      'MSc in Healthcare Quality PhD in Health Quality',
      'Organized Crime Prevention',
      'Rehabilitation and Health Leadership',
      'Rehabilitation Science',
      'Translational Medicine',
    ],
  },
] as const;

export function programsForQueensFaculty(facultyId: QueensFacultyId | null | undefined): readonly string[] {
  if (!facultyId) return [];
  const programs = QUEENS_FACULTY_OPTIONS.find((entry) => entry.id === facultyId)?.programs ?? [];
  return sortProgramsByVolume(facultyId, programs);
}

export function queensFacultyLabel(facultyId: QueensFacultyId | null | undefined): string | null {
  if (!facultyId) return null;
  return QUEENS_FACULTY_OPTIONS.find((entry) => entry.id === facultyId)?.label ?? null;
}
